// src/components/resources/specs/SpecsTable.tsx

import { clientLogger } from '~/lib/logger-client'
import { useState, useCallback } from 'react'
import RiPlayFill from '~/icons/remix/generated/RiPlayFill'
import RiTimeFill from '~/icons/remix/generated/RiTimeFill'
import RiRulerFill from '~/icons/remix/generated/RiRulerFill'
import RiDownloadFill from '~/icons/remix/generated/RiDownloadFill'
import RiClosedCaptioningFill from '~/icons/remix/generated/RiClosedCaptioningFill'
import RiVolumeUpFill from '~/icons/remix/generated/RiVolumeUpFill'
import RiVolumeMuteFill from '~/icons/remix/generated/RiVolumeMuteFill'
import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import RiStarFill from '~/icons/remix/generated/RiStarFill'
import RiEyeFill from '~/icons/remix/generated/RiEyeFill'
import RiMouseLine from '~/icons/remix/generated/RiMouseLine'
import RiShoppingCartLine from '~/icons/remix/generated/RiShoppingCartLine'
import RiUserAddLine from '~/icons/remix/generated/RiUserAddLine'
import RiHeartFill from '~/icons/remix/generated/RiHeartFill'
import RiFileVideoFill from '~/icons/remix/generated/RiFileVideoFill'
import RiFile3Fill from '~/icons/remix/generated/RiFile3Fill'
import RiFacebookFill from '~/icons/remix/generated/RiFacebookFill'
import RiInstagramFill from '~/icons/remix/generated/RiInstagramFill'
import RiTiktokLine from '~/icons/remix/generated/RiTiktokLine'
import RiPauseFill from '~/icons/remix/generated/RiPauseFill'
import RiCarouselView from '~/icons/remix/generated/RiCarouselView'
import RiHourglassFill from '~/icons/remix/generated/RiHourglassFill'
import RiLiveFill from '~/icons/remix/generated/RiLiveFill'
import RiRewindFill from '~/icons/remix/generated/RiRewindFill'
import RiVideoOnFill from '~/icons/remix/generated/RiVideoOnFill'
import { cn } from '~/lib/utils'
import { DevicePreview } from '~/components/ui/DevicePreview'
import { sendResourceEvent } from '~/lib/gtag'
import type { AdFormat, Platform } from '~/lib/resources/types/guides/specs'

// DevicePreview-compatible aspect ratio interface
interface DeviceAspectRatio {
  ratio: string
  width: number
  height: number
  recommended?: boolean
  id?: string
}

interface SpecsTableProps {
  formats: AdFormat[]
  className?: string
  guideSlug?: string // Added to track which guide the specs belong to
  /**
   * The guide's platform, used as the fallback for the per-row platform badge
   * when a format/placement name doesn't itself name a platform. Without this
   * the table would label every non-Instagram row "Facebook" (wrong for TikTok).
   */
  platform?: Platform
}

interface AspectRatio {
  ratio: string
  dimensions: {
    width: number
    height: number
  }
  recommended?: boolean
}

interface SpecsColumn {
  format: string
  placement: string
  description?: string
  detailedDescription?: string
  bestPractices?: string[]
  inheritsSourcePost?: boolean
  specs: {
    aspectRatios: AspectRatio[]
    resolutions: {
      min: { width: number; height: number }
      recommended?: { width: number; height: number }
    }
    fileTypes: string[]
    maxFileSize: { image?: number; video?: number }
    objectivesSupported: string[]
    captions: 'optional' | 'required' | 'recommended' | 'not-supported'
    sound: 'optional' | 'required' | 'recommended' | 'not-supported'
    safeZones: {
      top: number
      bottom: number
      left: number
      right: number
    }
    videoLength?: {
      min: number
      max: number
      recommended?: number
    }
    technicalRequirements?: string[]
  }
}

function formatFileSize(size: { image?: number; video?: number }): string {
  const parts: string[] = []
  
  const formatSize = (sizeInMB: number): string => {
    if (sizeInMB >= 1000) {
      const sizeInGB = sizeInMB / 1000
      return sizeInGB % 1 === 0 ? `${sizeInGB}GB` : `${sizeInGB.toFixed(1)}GB`
    }
    return `${sizeInMB}MB`
  }
  
  if (size.image) parts.push(`Image: ${formatSize(size.image)}`)
  if (size.video) parts.push(`Video: ${formatSize(size.video)}`)
  return parts.join(', ')
}

function formatVideoLength(length: { min: number; max: number; recommended?: number } | undefined): string {
  if (!length) return 'N/A'
  
  // Convert seconds to clear, readable format
  const formatDuration = (seconds: number): string => {
    if (seconds >= 3600) {
      // For long durations, use minutes for clarity
      const totalMinutes = Math.floor(seconds / 60)
      return `${totalMinutes} minutes`
    }
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      if (remainingSeconds > 0) {
        return `${minutes} minutes ${remainingSeconds} seconds`
      }
      return `${minutes} minutes`
    }
    return seconds === 1 ? `1 second` : `${seconds} seconds`
  }
  
  if (length.recommended) {
    return `${formatDuration(length.recommended)} recommended • ${formatDuration(length.min)} to ${formatDuration(length.max)} allowed`
  }
  return `${formatDuration(length.min)} to ${formatDuration(length.max)}`
}

/**
 * Get semantic icon for placement type to improve visual recognition
 */
function getPlacementIcon(placement: string): React.ComponentType<{ className?: string }> {
  const normalizedPlacement = placement.toLowerCase()
  
  if (normalizedPlacement.includes('single')) {
    return RiPlayFill // Single video content - standard play button
  }
  if (normalizedPlacement.includes('carousel')) {
    return RiCarouselView // Multiple videos in carousel format
  }
  if (normalizedPlacement.includes('stories')) {
    return RiHourglassFill // Stories format - temporary content
  }
  if (normalizedPlacement.includes('reels')) {
    return RiLiveFill // Reels format - short-form video content
  }
  if (normalizedPlacement.includes('pre-roll')) {
    return RiRewindFill // Play button for video ads that play before content
  }
  if (normalizedPlacement.includes('mid-roll')) {
    return RiPauseFill // Smaller play for mid-content interruption
  }
  if (normalizedPlacement.includes('feed') && normalizedPlacement.includes('video')) {
    return RiVideoOnFill // List/feed structure for timeline placement
  }
  
  return RiPlayFill // Default fallback
}

/**
 * Get meaningful icon for campaign objective types
 */
function getObjectiveIcon(objective: string): React.ComponentType<{ className?: string }> {
  const normalizedObjective = objective.toLowerCase()
  
  if (normalizedObjective.includes('awareness')) {
    return RiEyeFill // Eye for awareness/visibility
  }
  if (normalizedObjective.includes('traffic')) {
    return RiMouseLine // Mouse for clicks/traffic
  }
  if (normalizedObjective.includes('engagement')) {
    return RiHeartFill // Heart for engagement/interaction
  }
  if (normalizedObjective.includes('leads') || normalizedObjective.includes('app')) {
    return RiUserAddLine // User add for lead generation
  }
  if (normalizedObjective.includes('sales') || normalizedObjective.includes('promotion')) {
    return RiShoppingCartLine // Shopping cart for sales/conversions
  }
  
  return RiEyeFill // Default fallback
}

/**
 * Get icon for file type
 */
function getFileTypeIcon(fileType: string): React.ComponentType<{ className?: string }> {
  const normalizedType = fileType.toLowerCase()
  
  if (normalizedType.includes('mp4') || normalizedType.includes('mov')) {
    return RiFileVideoFill // Video file icon
  }
  if (normalizedType.includes('gif')) {
    return RiFile3Fill // Generic file for GIF
  }
  
  return RiFileVideoFill // Default to video icon
}

interface PlatformBadgeInfo {
  platform: Platform
  icon: React.ComponentType<{ className?: string }>
  color: string
  label: string
}

const PLATFORM_BADGES: Record<'facebook' | 'instagram' | 'tiktok', PlatformBadgeInfo> = {
  instagram: {
    platform: 'instagram',
    icon: RiInstagramFill,
    color: 'bg-gradient-to-r from-purple-500 to-pink-500',
    label: 'Instagram',
  },
  tiktok: {
    platform: 'tiktok',
    icon: RiTiktokLine,
    color: 'bg-gray-900 dark:bg-black',
    label: 'TikTok',
  },
  facebook: {
    platform: 'facebook',
    icon: RiFacebookFill,
    color: 'bg-blue-600',
    label: 'Facebook',
  },
}

/**
 * Determine the platform for a row's badge. A format/placement name that itself
 * names a platform (Meta's mixed Facebook/Instagram sections) wins; otherwise we
 * fall back to the guide's own platform so a TikTok guide doesn't label every
 * row "Facebook".
 */
function getPlatformInfo(
  format: string,
  placement: string,
  fallbackPlatform: Platform = 'facebook',
): PlatformBadgeInfo {
  const text = `${format} ${placement}`.toLowerCase()

  if (text.includes('instagram')) return PLATFORM_BADGES.instagram
  if (text.includes('tiktok')) return PLATFORM_BADGES.tiktok

  if (fallbackPlatform === 'instagram') return PLATFORM_BADGES.instagram
  if (fallbackPlatform === 'tiktok') return PLATFORM_BADGES.tiktok
  return PLATFORM_BADGES.facebook
}

/**
 * Enhanced requirement badge with proper icon and status indication
 */
function RequirementBadge({ 
  type, 
  status 
}: { 
  type: 'captions' | 'sound'
  status: 'optional' | 'required' | 'recommended' | 'not-supported'
}): JSX.Element {
  const config = {
    captions: {
      icon: RiClosedCaptioningFill,
      label: 'Captions'
    },
    sound: {
      icon: status === 'not-supported' ? RiVolumeMuteFill : RiVolumeUpFill,
      label: 'Sound'
    }
  }

  const { icon: Icon, label } = config[type]
  
  const statusConfig = {
    required: {
      className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800',
      text: 'Required'
    },
    recommended: {
      className: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800',
      text: 'Highly Recommended'
    },
    optional: {
      className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800',
      text: 'Optional'
    },
    'not-supported': {
      className: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
      text: 'Not Supported'
    }
  }

  const { className, text } = statusConfig[status]

  return (
    <div className={cn(
      "inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium",
      className
    )}>
      <Icon className="h-4 w-4" />
      <span>{label} {text}</span>
    </div>
  )
}

/**
 * Props for DevicePreviewCell component
 */
interface DevicePreviewCellProps {
  /** Specification data for the device preview */
  specs: SpecsColumn['specs']
  /** Currently selected aspect ratio */
  selectedRatio?: DeviceAspectRatio
  /** Callback when aspect ratio changes */
  onRatioChange?: (ratio: DeviceAspectRatio) => void
  /** Additional CSS classes */
  className?: string
  /** When true, the placement inherits its dimensions from an organic post, so
   * the fixed minimum/recommended resolution figures don't apply. */
  inheritsSourcePost?: boolean
}

/**
 * DevicePreviewCell component renders a device preview with aspect ratio information
 * and resolution requirements. Provides safe fallbacks for missing data.
 */
function DevicePreviewCell({
  specs,
  selectedRatio,
  onRatioChange,
  className,
  inheritsSourcePost,
}: DevicePreviewCellProps): JSX.Element {
  // Transform SpecsColumn aspect ratios to DevicePreview format
  const transformedAspectRatios: DeviceAspectRatio[] = specs.aspectRatios.map(ar => ({
    ratio: ar.ratio,
    width: ar.dimensions.width,
    height: ar.dimensions.height,
    recommended: ar.recommended,
    id: `${ar.ratio}-${ar.dimensions.width}x${ar.dimensions.height}`
  }))

  // Select current ratio with proper type safety and graceful fallbacks
  // Priority: selectedRatio -> recommended ratio -> first available ratio -> undefined
  const currentRatio = selectedRatio ?? 
    transformedAspectRatios.find(ar => ar.recommended) ?? 
    (transformedAspectRatios.length > 0 ? transformedAspectRatios[0] : undefined)

  // Check if safe zones are meaningful (any value > 0)
  const hasMeaningfulSafeZones = specs.safeZones && (
    specs.safeZones.top > 0 || 
    specs.safeZones.bottom > 0 || 
    specs.safeZones.left > 0 || 
    specs.safeZones.right > 0
  )

  return (
    <div className={cn("relative flex h-full w-full flex-col items-center justify-center p-8", className)}>
      <div className="relative flex w-full max-w-[400px] flex-col items-center space-y-6">
        <DevicePreview
          deviceType="mobile"
          aspectRatios={transformedAspectRatios}
          selectedRatio={currentRatio}
          onRatioChange={onRatioChange}
          safeZones={specs.safeZones}
          showSafeZones={hasMeaningfulSafeZones}
          showRatioSelectorButton={!inheritsSourcePost && transformedAspectRatios.length > 1}
          deviceLabel="iPhone 14 Pro"
          className="flex w-full flex-col items-center space-y-4"
        />
      </div>
      {/* Clean aspect ratio and resolution info */}
      <div className="mt-4 w-full max-w-[400px]">
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <h5 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            <RiRulerFill className="h-4 w-4 text-indigo-500" />
            Dimensions
          </h5>
          
          {/* Aspect ratios as clean list. Hidden for inherited placements
              (e.g. Spark Pull) — the boosted post's own ratio applies, so
              listing fixed ratios would imply a requirement that doesn't exist. */}
          {!inheritsSourcePost && (
          <div className="space-y-2 mb-4">
            {specs.aspectRatios.map(ar => (
              <div key={ar.ratio} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-3">
                  <div 
                    className="border-2 border-indigo-300 bg-indigo-100 dark:border-indigo-600 dark:bg-indigo-800 rounded"
                    style={{
                      width: `${Math.max(Math.min(ar.dimensions.width / 120, 18), 8)}px`,
                      height: `${Math.max(Math.min(ar.dimensions.height / 120, 18), 8)}px`
                    }}
                  />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {ar.ratio}
                  </span>
                  {ar.recommended && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                      <RiStarFill className="h-2.5 w-2.5" />
                      Best
                    </span>
                  )}
                </div>
                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                  {ar.dimensions.width}×{ar.dimensions.height}
                </span>
              </div>
            ))}
          </div>
          )}

          {/* Resolution requirements */}
          <div className={cn(!inheritsSourcePost && "pt-3 border-t border-gray-200 dark:border-gray-700")}>
            {inheritsSourcePost ? (
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Aspect ratio, resolution, and file type inherit the organic post — no ad-level requirement.
              </p>
            ) : (
            <div className="grid grid-cols-1 gap-2 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Minimum:</span>
                <span className="font-mono font-medium text-gray-900 dark:text-white">
                  {specs.resolutions.min.width}×{specs.resolutions.min.height}px
                </span>
              </div>
              {specs.resolutions.recommended && (
                <div className="flex justify-between">
                  <span className="text-emerald-600 dark:text-emerald-400">Best Quality:</span>
                  <span className="font-mono font-medium text-emerald-700 dark:text-emerald-300">
                    {specs.resolutions.recommended.width}×{specs.resolutions.recommended.height}px
                  </span>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function SpecsTable({ formats, className, guideSlug = 'meta-ad-creative-video-specs-guide', platform = 'facebook' }: SpecsTableProps): JSX.Element {
  // State for aspect ratio control with proper typing
  const [selectedRatios, setSelectedRatios] = useState<Record<string, DeviceAspectRatio>>({})

  // Transform formats into table rows and filter for valid video specs
  const rows = formats.flatMap(format =>
    format.placements.map(placement => ({
      format: format.name,
      placement: placement.name,
      description: placement.description,
      detailedDescription: placement.detailedDescription,
      bestPractices: placement.bestPractices,
      inheritsSourcePost: placement.inheritsSourcePost,
      specs: {
        ...placement.specs,
        // Ensure objectives are properly capitalized
        objectivesSupported: placement.specs.objectivesSupported.map(
          obj => obj.charAt(0).toUpperCase() + obj.slice(1).toLowerCase()
        ),
        // Leave videoLength absent when the data omits it. The previous
        // `?? {min:0,max:240}` fallback rendered a false "0 to 4 minutes" cap;
        // the duration cell below now distinguishes the two absent cases:
        // `inheritsSourcePost` placements (TikTok Spark Pull) inherit the
        // organic post's duration, while other placements with no declared cap
        // (Facebook Reels, whose ads-guide entry lists "No maximum limit.")
        // show "No fixed maximum listed".
        videoLength: placement.specs.videoLength
      }
    }))
  ).filter(column => {
    // Keep any row that carries renderable *video* specs. We deliberately do NOT
    // gate on the format NAME containing "video/reel/story/stream": that dropped
    // legitimately-video TikTok formats like "Spark Ads (Pull)", "TopView", and
    // "Brand Takeover", showing "No specifications available". The presence of
    // aspect ratios plus a video file type (mp4/mov/video) is the robust signal.
    const hasValidSpecs = column.specs &&
      column.specs.aspectRatios?.length > 0 &&
      column.specs.fileTypes?.length > 0 &&
      column.specs.fileTypes.some(type =>
        type.toLowerCase().includes('mp4') ||
        type.toLowerCase().includes('mov') ||
        type.toLowerCase().includes('video')
      )

    return hasValidSpecs
  }) as SpecsColumn[]

  // Handle aspect ratio changes with GA4 tracking
  const handleRatioChange = useCallback((specKey: string, ratio: DeviceAspectRatio) => {
    setSelectedRatios(prev => ({ ...prev, [specKey]: ratio }))
    
    const [format, placement] = specKey.split('::')
    if (!format || !placement) {
      clientLogger.warn('Invalid specKey format', { component: 'SpecsTable', specKey })
      return
    }
    
    sendResourceEvent('interact', {
      resourceType: 'guide',
      resourceSlug: guideSlug,
      params: {
        interaction_type: 'aspect_ratio_change',
        aspect_ratio: ratio.ratio,
        spec_format: format,
        spec_placement: placement
      }
    })
  }, [guideSlug])

  // Simple click handler for spec interactions
  const handleSpecClick = (specFormat: string, specPlacement: string): void => {
    sendResourceEvent('interact', {
      resourceType: 'guide',
      resourceSlug: guideSlug,
      params: {
        interaction_type: 'spec_view',
        spec_format: specFormat,
        spec_placement: specPlacement
      }
    })
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-lg bg-gray-50 p-8 text-center dark:bg-gray-800/50">
        <p className="text-gray-600 dark:text-gray-400">
          No specifications available for this format.
        </p>
      </div>
    )
  }

  return (
    <div className={cn("overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700", className)}>
      {/* SEO-enhanced semantic structure */}
      <div 
        className="divide-y divide-gray-200 dark:divide-gray-700"
        role="table"
        aria-label="Video ad format specifications"
      >
        {rows.map((row, index) => {
          const specKey = `${row.format}::${row.placement}`
          const selectedRatio = selectedRatios[specKey]
          const platformInfo = getPlatformInfo(row.format, row.placement, platform)
          
          return (
            <article
              key={specKey}
              className={cn(
                "grid grid-cols-1 divide-y divide-gray-200 lg:grid-cols-2 lg:divide-x lg:divide-y-0 dark:divide-gray-700 cursor-pointer transition-all duration-200 hover:shadow-md",
                index % 2 === 0 && "bg-gray-50 dark:bg-gray-800/50",
                "hover:bg-gray-100 dark:hover:bg-gray-700/50"
              )}
              onClick={() => handleSpecClick(row.format, row.placement)}
              itemScope
              itemType="https://schema.org/TechArticle"
              role="row"
              aria-label={`${row.format} ${row.placement} specifications`}
            >
            {/* Left Column - Technical Specifications */}
            <div className="space-y-6 p-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-xl",
                      platformInfo.platform === 'instagram'
                        ? "bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900"
                        : "bg-gradient-to-br from-indigo-100 to-blue-100 dark:from-indigo-900 dark:to-blue-900"
                    )}>
                      {(() => {
                        const IconComponent = getPlacementIcon(row.placement)
                        return <IconComponent className={cn(
                          "h-5 w-5",
                          platformInfo.platform === 'instagram'
                            ? "text-purple-600 dark:text-purple-400"
                            : "text-indigo-600 dark:text-indigo-400"
                        )} />
                      })()}
                    </div>
                    <div>
                                        <h3 
                    className="text-lg font-semibold text-gray-900 dark:text-white"
                    itemProp="name"
                  >
                    {row.placement}
                  </h3>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {row.format}
                  </p>
                    </div>
                  </div>
                  
                  {/* Platform badge for visual distinction */}
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold text-white",
                      platformInfo.color
                    )}>
                      <platformInfo.icon className="h-3.5 w-3.5" />
                      <span>{platformInfo.label}</span>
                    </div>
                  </div>
                </div>
                <p 
                  className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed"
                  itemProp="description"
                >
                  {row.detailedDescription ?? row.description ?? getPlacementDescription(row.format, row.placement)}
                </p>
              </div>
              <div className="space-y-6">
                {/* Video Duration with improved visual treatment */}
                <div>
                  <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <RiTimeFill className="h-4 w-4 text-blue-500" />
                    Video Duration
                  </h4>
                  <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 dark:bg-blue-900/20 dark:border-blue-800">
                    <div className="flex items-center gap-2">
                      <RiTimeFill className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <div className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        {row.specs.videoLength
                          ? formatVideoLength(row.specs.videoLength)
                          : row.inheritsSourcePost
                            ? 'No fixed limit — inherits the source post'
                            : 'No fixed maximum listed'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Media Requirements with enhanced badges */}
                <div>
                  <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <RiCheckLine className="h-4 w-4 text-green-500" />
                    Media Requirements
                  </h4>
                  <div className="flex flex-wrap gap-3">
                    <RequirementBadge type="captions" status={row.specs.captions} />
                    <RequirementBadge type="sound" status={row.specs.sound} />
                  </div>
                </div>

                {/* File Specifications with clear hierarchy and icons */}
                <div>
                  <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <RiDownloadFill className="h-4 w-4 text-green-500" />
                    File Requirements
                  </h4>
                  {row.inheritsSourcePost ? (
                    // Spark Pull and similar "boost an organic post" placements
                    // carry no ad-level file constraints — show an inherited
                    // note rather than hard limits that could mislabel a valid
                    // organic post as non-compliant.
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 dark:bg-amber-900/20 dark:border-amber-800">
                      <p className="text-sm text-amber-900 dark:text-amber-100">
                        <span className="font-semibold">Inherited from the organic post.</span>{' '}
                        TikTok sets no ad-level limit on file type, file size, or resolution for these ads — the creative reuses whatever the boosted post already is.
                      </p>
                    </div>
                  ) : (
                  <div className="space-y-4">
                    {/* File size - most important constraint */}
                    <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 dark:bg-green-900/20 dark:border-green-800">
                      <div className="flex items-center gap-2">
                        <RiDownloadFill className="h-4 w-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-semibold text-green-900 dark:text-green-100">
                          {formatFileSize(row.specs.maxFileSize)} maximum
                        </span>
                      </div>
                    </div>

                    {/* Supported file types with individual icons */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        Supported Formats
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {row.specs.fileTypes.map(type => {
                          const IconComponent = getFileTypeIcon(type)
                          return (
                            <div key={type} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800">
                              <IconComponent className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                              <span className="text-sm font-medium text-indigo-900 dark:text-indigo-100">
                                {type}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                  )}
                </div>

                {/* Campaign Objectives with meaningful icons */}
                <div>
                  <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <RiStarFill className="h-4 w-4 text-purple-500" />
                    Campaign Objectives
                  </h4>
                  <div className="grid grid-cols-1 gap-2">
                    {row.specs.objectivesSupported.map(objective => {
                      const IconComponent = getObjectiveIcon(objective)
                      return (
                        <div key={objective} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-purple-50 border border-purple-200 dark:bg-purple-900/20 dark:border-purple-800">
                          <IconComponent className="h-4 w-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                            {objective}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Technical Requirements for SEO content */}
                {row.specs.technicalRequirements && row.specs.technicalRequirements.length > 0 && (
                  <div>
                    <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                      <RiCheckLine className="h-4 w-4 text-indigo-500" />
                      Technical Requirements
                    </h4>
                    <div className="space-y-2">
                      {row.specs.technicalRequirements.map((requirement: string, idx: number) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <span className="text-indigo-500 mt-1">•</span>
                          <span className="leading-relaxed">{requirement}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Best Practices from data for SEO content */}
                {row.bestPractices && row.bestPractices.length > 0 && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                      <RiStarFill className="h-4 w-4 text-amber-500" />
                      Best Practices
                    </h4>
                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      {row.bestPractices.map((practice: string, idx: number) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          <span className="leading-relaxed">{practice}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

              {/* Right Column - Device Preview */}
              <DevicePreviewCell
                specs={row.specs}
                selectedRatio={selectedRatio}
                onRatioChange={(ratio) => handleRatioChange(specKey, ratio)}
                className="border-t lg:border-t-0"
                inheritsSourcePost={row.inheritsSourcePost}
              />
            </article>
          )
        })}
      </div>
    </div>
  )
}

function getPlacementDescription(format: string, placement: string): string {
  const descriptions: Record<string, Record<string, string>> = {
    'Feed Video': {
      'Single Video': 'Individual video ads displayed in the Facebook News Feed alongside organic content.',
      'Video Carousel': 'Multiple videos in a swipeable carousel format for the News Feed.'
    },
    'Facebook Stories': {
      'Facebook Stories': 'Full-screen vertical video ads that appear between organic Facebook Stories.'
    },
    'Facebook Reels': {
      'Facebook Reels': 'Vertical video ads that appear between organic Reels content on Facebook.'
    },
    'In-Stream Video': {
      'Pre-Roll': 'Video ads that play before video content begins.',
      'Mid-Roll': 'Video ads that play during video content.'
    },
    'Facebook Video Feeds': {
      'Facebook Video Feed': 'Video ads that appear between organic videos in the Video Feed section.'
    },
    'Instagram Feed': {
      'Instagram Feed': 'Video ads that appear in the Instagram Feed alongside organic content.'
    },
    'Instagram Stories': {
      'Instagram Stories': 'Full-screen vertical video ads that appear between organic Instagram Stories.'
    },
    'Instagram Reels': {
      'Instagram Reels': 'Vertical video ads that appear between organic Reels content on Instagram.'
    }
  }

  return descriptions[format]?.[placement] ?? 'Video ad format for Meta platforms.'
} 